/**
 * InflowPay SDK v2 - Iframe-based Payment SDK
 *
 * This SDK creates an iframe and communicates with a React payment application
 * using postMessage API for secure cross-origin communication.
 */

import { DIMENSIONS } from './constants/sdk-constants';
import type { IframeMessage, PaymentConfig, PaymentError, SDKConfig, TransactionData } from './types';
import { PaymentResultErrorCode } from './types';
import { ApplePaySheetOverlay } from './ui/apple-pay-sheet-overlay';
import { LoaderManager } from './ui/loader-manager';
import { ModalManager } from './ui/modal-manager';
import { OverlayManager } from './ui/overlay-manager';
import { getInlineIframeStyles } from './ui/styles';
import { getEnvironmentFromApiKey, getIframeUrlFromApiKey } from './utils';

/** Resolves width mode from `options.fillParent`, then `style.fillParent`. */
function resolveFillParent(config?: PaymentConfig): boolean {
  if (config?.options?.fillParent !== undefined) {
    return config.options.fillParent === true;
  }
  return config?.style?.fillParent === true;
}

/**
 * How long the SDK waits for the iframe's `iframe-ready` message before giving
 * up. Generous enough for a slow network on a cold cache; short enough that a
 * frame the browser refused outright does not leave the buyer on a shimmer.
 */
const IFRAME_READY_TIMEOUT_MS = 15_000;

/**
 * The merchant page's own `document.referrer`, or undefined when unavailable
 * (no referrer, non-browser runtime, or a browser that throws on access).
 */
function readPageReferrer(): string | undefined {
  try {
    const raw = typeof document === 'undefined' ? '' : document.referrer;
    return raw ? raw.slice(0, 2048) : undefined;
  } catch {
    return undefined;
  }
}

const IFRAME_NOT_READY_MESSAGE =
  'The Inflow payment form could not load on this page. If this domain is not verified on your Inflow account, add it under Settings → Domains in your dashboard, or contact support.';

export class SDK {
  private iframe: HTMLIFrameElement | null = null;
  private iframeUrl: string;
  private config: SDKConfig & { iframeUrl?: string; debug?: boolean };
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private containerElement: HTMLElement | null = null;
  private usePopup: boolean;
  private environment: 'local' | 'sandbox' | 'production' | 'development' | 'preprod';
  private loaderManager: LoaderManager | null = null;
  private modalManager: ModalManager;
  private readyTimeout?: ReturnType<typeof setTimeout>;
  private applePaySheet = new ApplePaySheetOverlay();
  /** Latest form height reported while the Apple Pay sheet covered the page. */
  private heightWhileSheetOpen: number | null = null;

  constructor(config: SDKConfig & { iframeUrl?: string; debug?: boolean }) {
    this.config = config;

    // Auto-detect iframe URL from API key, or use provided iframeUrl (internal use only)
    this.iframeUrl = config.iframeUrl || getIframeUrlFromApiKey(config.publicKey || '');
    this.environment = getEnvironmentFromApiKey(config.publicKey || '');

    // Determine if we should use popup or inline
    this.usePopup = !config.container;

    // Resolve container if provided
    if (config.container) {
      if (typeof config.container === 'string') {
        this.containerElement = document.querySelector(config.container);
        if (!this.containerElement) {
          throw new Error(`Container not found: ${config.container}`);
        }
      } else {
        this.containerElement = config.container;
      }
    }

    // Initialize modal manager
    this.modalManager = new ModalManager({
      environment: this.environment,
      debug: this.config.debug,
    });
  }

  /**
   * Initialize and open the payment iframe
   */
  init(): void {
    if (this.iframe) {
      return;
    }

    this.createIframe();
    this.addMessageListener();
  }

  /**
   * Create and append the iframe to the document
   */
  private createIframe(): void {
    // Build iframe URL with API key and paymentId and locale as query parameters
    const url = new URL(this.iframeUrl);
    if (this.config.publicKey) {
      url.searchParams.set('publicKey', this.config.publicKey);
    }
    if (this.config.config?.paymentId) {
      url.searchParams.set('paymentId', this.config.config.paymentId);
    }
    if (this.config.config?.setupId) {
      url.searchParams.set('setupId', this.config.config.setupId);
    }
    if (this.config.locale) {
      url.searchParams.set('locale', this.config.locale);
    }
    const iframeSrc = url.toString();

    if (this.usePopup) {
      // Use OverlayManager to create popup UI
      const { overlay, container, iframe } = OverlayManager.createOverlay(
        iframeSrc,
        () => this.close()
      );

      this.iframe = iframe;
      document.body.appendChild(overlay);

      // Show loader
      const fillParent = resolveFillParent(this.config.config);
      this.loaderManager = new LoaderManager(container, {
        fillParent,
        loaderColor: this.config.config?.appearance?.variables?.loaderColor,
      });
      this.loaderManager.show(this.iframe);
    } else {
      // Inline mode - mount directly in container
      if (!this.containerElement) {
        throw new Error('Container element is required for inline mode');
      }

      // Clear container
      this.containerElement.innerHTML = '';

      // Set container styles for seamless integration
      if (this.containerElement instanceof HTMLElement) {
        const currentStyle = this.containerElement.getAttribute('style') || '';
        if (!currentStyle.includes('min-height')) {
          this.containerElement.style.minHeight = `${DIMENSIONS.MIN_HEIGHT}px`;
        }
        if (!currentStyle.includes('position')) {
          this.containerElement.style.position = 'relative';
        }
        if (!currentStyle.includes('overflow')) {
          this.containerElement.style.overflow = 'hidden';
        }
      }

      const fillParent = resolveFillParent(this.config.config);

      // Create iframe
      this.iframe = document.createElement('iframe');
      this.iframe.src = iframeSrc;
      this.iframe.style.cssText = getInlineIframeStyles(fillParent);
      this.iframe.setAttribute('allow', 'payment');

      // Append to container
      this.containerElement.appendChild(this.iframe);

      // Show loader
      this.loaderManager = new LoaderManager(this.containerElement, {
        fillParent,
        loaderColor: this.config.config?.appearance?.variables?.loaderColor,
      });
      this.loaderManager.show(this.iframe);
    }

    // The frame is mounted and the loader is up: from here the iframe owes us
    // an `iframe-ready`, and silence has to surface as an error.
    this.startReadyTimeout();
  }

  /**
   * Arm the readiness watchdog.
   *
   * `iframe-ready` is the only signal that script inside the iframe is alive.
   * When the embedding page's domain is not verified on the merchant's account,
   * the checkout's `frame-ancestors` policy makes the browser refuse the frame
   * outright: nothing loads, no message is ever posted, and before this timer
   * existed the skeleton shimmer ran forever while `onError` stayed silent.
   */
  private startReadyTimeout(): void {
    this.clearReadyTimeout();
    this.readyTimeout = setTimeout(() => {
      this.readyTimeout = undefined;
      this.handleReadyTimeout();
    }, IFRAME_READY_TIMEOUT_MS);
  }

  private clearReadyTimeout(): void {
    if (this.readyTimeout !== undefined) {
      clearTimeout(this.readyTimeout);
      this.readyTimeout = undefined;
    }
  }

  /**
   * Readiness deadline passed: stop pretending the form is still coming and
   * report it on the same channel as an iframe-reported error.
   */
  private handleReadyTimeout(): void {
    // A shimmer that never stops reads as "still working" to the buyer.
    this.loaderManager?.hide(this.iframe ?? undefined);

    if (this.config.debug) {
      console.warn(
        `[SDK] Iframe did not report ready within ${IFRAME_READY_TIMEOUT_MS}ms. ` +
          `Check that ${window.location.origin} is a verified domain on this Inflow account.`
      );
    }

    const error: PaymentError = {
      code: PaymentResultErrorCode.IFRAME_NOT_READY,
      message: IFRAME_NOT_READY_MESSAGE,
      retryable: false,
      origin: window.location.origin,
    };

    this.config.onError?.(error);
  }

  /**
   * Add message listener for communication with iframe
   */
  private addMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      const allowedOrigin = new URL(this.iframeUrl).origin;
      const isExactMatch = event.origin === allowedOrigin;

      let isAllowedOrigin = isExactMatch;

      if (!isAllowedOrigin) {
        if (this.environment === 'sandbox' || this.environment === 'development') {
          const isLocalhostDev =
            (event.origin.includes('localhost') || event.origin.includes('127.0.0.1')) &&
            (allowedOrigin.includes('localhost') || allowedOrigin.includes('127.0.0.1'));
          isAllowedOrigin = isLocalhostDev;
        }

        if (!isAllowedOrigin) {
          const isAllowedApiOrigin =
            event.origin === 'https://dev.iframe.inflowpay.com' ||
            event.origin === 'https://pre-prod.iframe.inflowpay.xyz' ||
            event.origin === 'https://iframe.inflowpay.xyz';
          isAllowedOrigin = isAllowedApiOrigin;
        }
      }

      if (!isAllowedOrigin) {
        if (this.config.debug) {
          console.warn('[SDK] Rejected message from unauthorized origin:', event.origin);
        }
        return;
      }

      const data = event.data as IframeMessage;

      if (!data || !data.type) {
        return;
      }

      switch (data.type) {
        case 'iframe-ready':
          // The frame is alive: the readiness watchdog has nothing to report.
          this.clearReadyTimeout();
          // Wait for iframe's javascript to be ready before sending config
          if (this.loaderManager) {
            this.loaderManager.hide(this.iframe || undefined);
          }
          this.sendConfigToIframe();
          break;

        case 'content-height':
          if (!data.height) break;
          // While the Apple Pay sheet overlay is open, defer the height and apply it on close.
          if (this.applePaySheet.isOpen) {
            this.heightWhileSheetOpen = data.height;
          } else {
            this.applyContentHeight(data.height);
          }
          break;

        case 'apple-pay-sheet':
          if (data.open === true && this.iframe) {
            this.applePaySheet.open(this.iframe);
          } else if (data.open === false) {
            this.closeApplePaySheet();
          }
          break;

        case 'close':
          this.close();
          break;

        case 'success':
          this.closeApplePaySheet();
          if (this.config.onSuccess) {
            this.config.onSuccess(data.data);
          }
          break;

        case 'error':
          this.closeApplePaySheet();
          if (this.config.onError) {
            this.config.onError(data.data);
          }
          break;

        case '3ds-required':
          // Iframe requests SDK to open 3DS popup
          if (this.config.debug) {
            console.log('[SDK] Received 3DS request:', data.threeDsSessionUrl);
            console.log('[SDK] Session ID:', data.sessionId);
          }
          if (data.threeDsSessionUrl) {
            const sessionId = data.sessionId;
            const requestId =
              data.paymentId ||
              data.setupId ||
              this.config.config?.paymentId ||
              this.config.config?.setupId ||
              '';

            if (!data.sessionId) {
              console.warn('[SDK] No session ID provided by iframe');
            }

            if (this.config.debug) {
              console.log('[SDK] Opening 3DS modal with session ID:', sessionId);
            }

            this.modalManager.open(data.threeDsSessionUrl, sessionId as string, requestId).then((success) => {
              if (this.config.debug) {
                console.log('[SDK] 3DS modal closed, result:', success);
              }
              // No longer send message back to iframe - unidirectional flow
            });
          } else {
            if (this.config.debug) {
              console.error('[SDK] 3DS required but no threeDsSessionUrl provided');
            }
          }
          break;

        case '3ds-success':
          // 3DS challenge completed via WebSocket, close modal automatically
          if (this.config.debug) {
            console.log('[SDK] 3DS completed, closing modal...');
            console.log('[SDK] Session ID from webhook:', data.sessionId);
          }
          // Pass session ID to validate before closing
          ModalManager.close(data.sessionId);
          break;

        case '3ds-failed':
          // 3DS challenge completed via WebSocket, close modal automatically
          if (this.config.debug) {
            console.log('[SDK] 3DS failed, closing modal...');
            console.log('[SDK] Session ID from webhook:', data.sessionId);
          }
          // Pass session ID to validate before closing
          ModalManager.close(data.sessionId);
          break;

        default:
          if (this.config.debug) {
            console.log('SDK: Received message:', data);
          }


      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /** Size the iframe (and inline container) to the form's reported height. */
  private applyContentHeight(height: number): void {
    if (!this.iframe) return;
    this.iframe.style.height = `${height}px`;
    if (this.containerElement) {
      this.containerElement.style.minHeight = `${height}px`;
    }
  }

  /** Restore the iframe after the Apple Pay sheet closes and apply the latest reported height. */
  private closeApplePaySheet(): void {
    if (!this.applePaySheet.isOpen) return;
    this.applePaySheet.close();
    if (this.heightWhileSheetOpen !== null) {
      this.applyContentHeight(this.heightWhileSheetOpen);
      this.heightWhileSheetOpen = null;
    }
  }

  /**
   * Send configuration to the iframe
   */
  private sendConfigToIframe(): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      // Wait for iframe to load
      if (this.iframe) {
        this.iframe.onload = () => {
          this.sendConfigToIframe();
        };
      }
      return;
    }

    const message: IframeMessage = {
      type: 'sdkData',
      config: {
        ...(this.config.config || {}),
        paymentId: this.config.config?.paymentId,
        setupId: this.config.config?.setupId,
        // osDarkModeDefaults: false — skip OS prefers-color-scheme dark token defaults.
        // SDKs through @inflow_pay/sdk@1.5.0 omit this flag and keep OS dark defaults.
        // Merchant appearance/style overrides still apply either way.
        osDarkModeDefaults: false,
        // applePaySheetOverlay: true — lift the iframe over the page while Apple's QR sheet
        // is open (see 'apple-pay-sheet'). SDKs through @inflow_pay/sdk@1.8.1 omit this flag;
        // the iframe then offers Apple Pay only where the browser has its own sheet (Safari, iOS).
        applePaySheetOverlay: true,
      },
      data: {
        publicKey: this.config.publicKey,
        // Where the buyer came from BEFORE this page. Only this script, running
        // in the merchant's page, can read it; the iframe (another origin)
        // cannot. Inflow stores the origin only, as an investigation signal.
        pageReferrer: readPageReferrer(),
      },
    };
    const targetOrigin = this.getTargetOrigin();
    this.iframe.contentWindow.postMessage(message, targetOrigin);
  }


  /**
   * Close the iframe and cleanup
   */
  private close(): void {
    this.clearReadyTimeout();
    this.closeApplePaySheet();

    if (this.config.onClose) {
      this.config.onClose();
    }

    // Hide loader
    if (this.loaderManager) {
      this.loaderManager.hide(this.iframe || undefined);
      this.loaderManager = null;
    }

    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.usePopup) {
      // Remove overlay
      OverlayManager.removeOverlay();
    } else {
      // Clear container
      if (this.containerElement) {
        this.containerElement.innerHTML = '';
      }
    }

    this.iframe = null;
  }

  /**
   * Handle success state: when showDefaultSuccessUI is true, iframe shows success UI
   * and we keep it mounted; when false, we unmount so the parent can render their own.
   */
  public switchToSuccessState(_result: TransactionData): void {
    this.clearReadyTimeout();

    // Hide loader
    if (this.loaderManager) {
      this.loaderManager.hide(this.iframe || undefined);
      this.loaderManager = null;
    }

    if (this.usePopup) {
      if (this.messageListener) {
        window.removeEventListener('message', this.messageListener);
        this.messageListener = null;
      }
      OverlayManager.removeOverlay();
      this.iframe = null;
      return;
    }

    const showDefault = this.config.config?.showDefaultSuccessUI ?? true;

    if (!showDefault && this.containerElement) {
      this.containerElement.innerHTML = '';
      this.iframe = null;
    }
    // When showDefault is true, keep iframe mounted (success UI is shown inside iframe)
  }

  /**
   * Get target origin for postMessage based on environment
   * In production/pre-prod: use exact origin for security
   * In dev/sandbox: use wildcard for development flexibility
   */
  private getTargetOrigin(): string {
    if (this.environment === 'production' || this.environment === 'preprod') {
      return new URL(this.iframeUrl).origin;
    }
    return '*';
  }

  /**
   * Public method to close the iframe
   */
  public destroy(): void {
    this.close();
  }
}

